import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { trpc } from "@/trpc/client";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { z } from "zod";
import { Role } from "@logistics-platform/types";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { User, Mail, Lock, Phone, Loader2, Eye, EyeOff, Truck } from "lucide-react";

// Simplified schema for driver invite - role is fixed
const DriverRegisterSchema = z.object({
    fullName: z.string().min(3, "Full name must be at least 3 characters."),
    email: z.string().email("Please enter a valid email address."),
    phoneOne: z.string().min(9, "Please enter a valid phone number."),
    phoneTwo: z.string().optional(),
    password: z.string().min(6, "Password must be at least 6 characters."),
    confirmPassword: z.string().min(6, "Please confirm your password."),
}).refine(data => data.password === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
});

type TDriverRegisterSchema = z.infer<typeof DriverRegisterSchema>;

const DriverRegister = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fleetOwnerId = searchParams.get("ref");

    const { login } = useAuthStore();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Redirect if no invite code? Or just alert.
    useEffect(() => {
        if (!fleetOwnerId) {
            toast.error("Invalid invite link. Missing Fleet Manager reference.");
            navigate("/register"); // Redirect to normal register
        }
    }, [fleetOwnerId, navigate]);

    const form = useForm<TDriverRegisterSchema>({
        resolver: zodResolver(DriverRegisterSchema),
        defaultValues: { fullName: "", email: "", phoneOne: "", phoneTwo: "", password: "", confirmPassword: "" },
    });

    const { mutate, isLoading } = trpc.user.register.useMutation({
        onSuccess: (data) => {
            setIsSubmitting(false);
            login(data);
            toast.success("Driver account created! Welcome to the Fleet.");
            navigate("/dashboard");
        },
        onError: (error) => {
            setIsSubmitting(false);
            toast.error(error.message);
        },
    });

    const onSubmit = (data: TDriverRegisterSchema) => {
        if (!fleetOwnerId) return;
        setIsSubmitting(true);
        mutate({
            ...data,
            email: data.email.trim().toLowerCase(),
            role: Role.FLEET_DRIVER, // Forced Role
            fleetOwnerId: fleetOwnerId
        });
    };

    if (!fleetOwnerId) return null;

    return (
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] py-6 px-4 animate-fade-in">
            <Card className="w-full max-w-md glass border-primary/20 shadow-lg shadow-primary/5">
                <CardHeader className="text-center space-y-2">
                    <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                        <Truck className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-2xl font-bold">
                        Join Fleet
                    </CardTitle>
                    <CardDescription>
                        You have been invited to join a Logistics Fleet.<br />
                        Create your driver account below.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="fullName"
                                render={({ field }) => (
                                    <FormItem>
                                        <Label>Full Name</Label>
                                        <FormControl>
                                            <div className="relative">
                                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input placeholder="Your real name" {...field} className="pl-10" />
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <Label>Email</Label>
                                        <FormControl>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input type="email" placeholder="email@domain.com" {...field} className="pl-10" />
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="phoneOne"
                                    render={({ field }) => (
                                        <FormItem>
                                            <Label>Phone</Label>
                                            <FormControl>
                                                <div className="relative">
                                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                    <Input type="tel" {...field} className="pl-10" />
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="phoneTwo"
                                    render={({ field }) => (
                                        <FormItem>
                                            <Label>Alt. Phone</Label>
                                            <FormControl>
                                                <div className="relative">
                                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                    <Input type="tel" {...field} className="pl-10" />
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <Label>Password</Label>
                                        <FormControl>
                                            <div className="relative">
                                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input
                                                    type={showPassword ? "text" : "password"}
                                                    {...field}
                                                    className="pl-10 pr-10"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                >
                                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="confirmPassword"
                                render={({ field }) => (
                                    <FormItem>
                                        <Label>Confirm Password</Label>
                                        <FormControl>
                                            <div className="relative">
                                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    {...field}
                                                    className="pl-10 pr-10"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                >
                                                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button
                                type="submit"
                                variant="premium"
                                className="w-full mt-4"
                                disabled={isLoading || isSubmitting}
                            >
                                {isLoading || isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Joining Fleet...
                                    </>
                                ) : (
                                    "Create Driver Account"
                                )}
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
};

export default DriverRegister;
